import classNames from 'classnames'
import {Formik} from 'formik'
import React, {useEffect, useMemo, useState} from 'react'
import {useSelector} from 'react-redux'
import Badge from '../../../components/core/badge'
import {SimpleBlueButton} from '../../../components/core/button'
import ButtonLoader from '../../../components/core/button/ButtonLoader'
import Switch from '../../../components/core/switch'
import Tooltip from '../../../components/core/tooltip'
import PieProgressChart from '../../../components/pages/pieProgressChart'
import plans from '../../../data/plans.json'
import os from '../../../data/selectOptions/os.json'
import serverMetrics from '../../../data/serverMetrics.json'
import usageLimits from '../../../data/usageLimits.json'
import {NewInfoIcon} from '../../../icons'
import {colors} from '../../../utils/colors'
import limitUsageManager from '../../../utils/hooks/limitUsageManager'
import renderSkeletonLoaders from '../../../utils/loader'
import {getImageSource} from '../../../utils/object'
import TextHelper from '../../../utils/text'
import text from '../text.json'
import DynamicForm from './DynamicForm'
import './index.scss'
import RulesetListView from '../../../components/pages/rulesetIntervalForm/RulesetListView'
import ConfigFileComponent from '../../../components/pages/serverMonitoring/serverConfig/ConfigFileComponent'

const serviceTypes = {
    selection_type: 'selection',
    form_type: 'form',
    instructions_type: 'instructions',
    chart_type: 'chart',
    metrics_type: 'metrics',
    rulesets_type: 'rulesets',
    completion_type: 'completion'
}

const MultiForm = ({
    monitoringType,
    monitoringTypeData,
    selectedItem,
    setSelectedItem,
    onChange,
    formikRef,
    formSchema,
    initialValuesMap,
    setInitialFormValues,
    setInitialValuesMap,
    defaultFormValues,
    errors,
    disabled,
    instructionOptions,
    isLoading,
    ...props
}) => {
    const [selectionTypeElements, setSelectionTypeElements] = useState([])
    const [chartTypeElements, setChartTypeElements] = useState([])
    const [isImageLoading, setIsImageLoading] = useState(false)
    const formType = useMemo(() => Object.values(monitoringTypeData)?.[0], [])

    const organisationLimitUsages = useSelector(
        (state) => state.auth.organisationLimitUsages
    )
    const isDarkMode = useSelector((state) => state.sidebar.isDarkMode)
    const currentPlan = localStorage.getItem('plan')

    const getSelectionTypeElements = async() => {
        setIsImageLoading(true)
        const itemsWithImages = await Promise.all(
            monitoringTypeData.map(async(item) => {
                const image = await getImageSource(
                    selectedItem?.key === item?.key
                        ? item?.image?.active
                        : item?.image?.default
                )
                return {
                    ...item,
                    image
                }
            })
        )

        setSelectionTypeElements(itemsWithImages)
        setIsImageLoading(false)
    }

    const getChartTypeElements = async() => {
        setIsImageLoading(true)
        const itemsWithImages = await Promise.all(
            monitoringTypeData?.map(async(item) => {
                const image = await getImageSource(item?.chart_image)
                return {
                    ...item,
                    chart_image: image
                }
            })
        )

        setChartTypeElements(itemsWithImages)
        setIsImageLoading(false)
    }

    const generateSelectionType = () => {
        return isImageLoading
            ? renderSkeletonLoaders(1, 'select-item-loader')
            : selectionTypeElements?.map(
                (item, index) =>
                    item?.enabled &&
            (isLoading ? (
                renderSkeletonLoaders(1, 'select-item-loader')
            ) : (
                <div
                    key={index}
                    className={classNames('selection-type-container', {
                        active: selectedItem?.key === item?.key,
                        disabled: disabled
                    })}
                    onClick={() =>
                        onChange({
                            key: item.key,
                            next_step: item?.next_step
                        })
                    }
                    tabIndex={0}
                >
                    <span className="selection-item">
                        <span className="item-image">
                            <img src={item.image} alt="pinghome" />
                        </span>
                        <div className="form-content">
                            <span className="item-title">{item?.title}</span>
                            <span className="item-desc">{item?.description}</span>
                        </div>
                    </span>
                    <input
                        type="radio"
                        checked={selectedItem?.key === item.key}
                        disabled={disabled}
                    />
                </div>
            ))
            )
    }

    const generateChartType = () => {
        return isImageLoading
            ? renderSkeletonLoaders(2, 'selection-type-container chart-container')
            : chartTypeElements?.map(
                (item, index) =>
                    item.enabled && (
                        <div
                            key={index}
                            className={classNames(
                                'selection-type-container chart-container',
                                {
                                    active: selectedItem === item.chart_type
                                }
                            )}
                            onClick={() => {
                                onChange(item?.chart_type)
                            }}
                        >
                            {isLoading ? (
                                renderSkeletonLoaders(1, 'selection-item')
                            ) : (
                                <>
                                    <span className="selection-item">
                                        <img src={item.chart_image} alt="pinghome" />
                                    </span>
                                    <span className="chart-title">{item?.title}</span>
                                </>
                            )}
                        </div>
                    )
            )
    }

    const generateMetricsType = () => {
        return (
            <>
                {Object.entries(monitoringTypeData).map(([type, item]) => {
                    const {value} = limitUsageManager(
                        usageLimits.limit_modules.monitoring.module,
                        usageLimits.limit_keys?.[item?.limit_key]?.limit_key,
                        organisationLimitUsages
                    )
                    const metricsTypeItem = props?.selectedMetricTypes?.[type]
                    const isLimitExceeded = metricsTypeItem?.usage >= value

                    return (
                        <>
                            <div
                                key={type}
                                className={classNames('metrics-items-container', {
                                    [props?.metricsClassName]: !!props?.metricsClassName
                                })}
                            >
                                <span className="metrics-types">
                                    <span className="metrics-title">
                                        <h3>{item?.block_title}</h3>
                                        {!item?.enabled && item?.badge_text && (
                                            <Badge color="primary">{item.badge_text}</Badge>
                                        )}
                                    </span>
                                    <span className="metrics-types-items">
                                        {item.child_items.map((childItem) =>
                                            renderMetricItem(
                                                type,
                                                item?.enabled,
                                                childItem,
                                                isLimitExceeded,
                                                metricsTypeItem
                                            )
                                        )}
                                    </span>
                                </span>
                                <span className="metrics-types-usage">
                                    <PieProgressChart
                                        value={metricsTypeItem?.usage}
                                        max={value}
                                        config={{
                                            tooltip_text: text.usage_text,
                                            label_text: text.usage
                                        }}
                                    />
                                </span>
                            </div>
                        </>
                    )
                })}

                {props?.advancedSettingsContainer && (
                    <div
                        className={classNames(
                            'metrics-items-container interval-based-ruleset',
                            {
                                dark: isDarkMode
                            }
                        )}
                    >
                        {props.advancedSettingsContainer}
                    </div>
                )}
                <div className="upgrade-info-container">
                    <span
                        dangerouslySetInnerHTML={{
                            __html: TextHelper.replaceAllChars(
                                text.to_increase_your_limits_text,
                                ':upgrade_text',
                                currentPlan === plans.pro.key
                                    ? text.contact_mail_text
                                    : text.upgrade_plan_text
                            )
                        }}
                        className="metrics-items-container"
                    />
                    <span className="button-container">
                        {props?.isExpandable && (
                            <SimpleBlueButton
                                className="save-button"
                                disabled={props?.isButtonDisabled || props?.isButtonLoading}
                                onClick={props?.updateServer}
                            >
                                {text.save}
                                {props?.isButtonLoading && <ButtonLoader />}
                            </SimpleBlueButton>
                        )}
                    </span>
                </div>
            </>
        )
    }

    const renderMetricItem = (
        type,
        isItemEnabled,
        childItem,
        isLimitExceeded,
        metricsTypeItem
    ) => {
        const isDisabled = isChildItemDisabled(childItem, type, isLimitExceeded)
        const disabledTooltip = getDisabledTooltip(
            childItem,
            type,
            isLimitExceeded
        )

        return (
            <span
                className={classNames('metrics-types-item', {disabled: isDisabled})}
                key={childItem.value}
            >
                <span className="item-name">
                    <Tooltip
                        placement="top"
                        trigger="mouseenter"
                        content={disabledTooltip || null}
                    >
                        <Switch
                            checked={
                                metricsTypeItem?.child_items?.some(
                                    (item) => item?.value === childItem.value
                                ) && !isSwapDisabled(childItem, type, isLimitExceeded)
                            }
                            onChange={() => onChange(type, childItem.value, isDisabled)}
                            isDisabled={isDisabled || !isItemEnabled}
                        />
                    </Tooltip>
                    <span className="child-item-name">{childItem.name}</span>
                </span>
                <Tooltip placement="left" trigger="click" content={childItem.info_text}>
                    <NewInfoIcon
                        width={20}
                        height={20}
                        fillColor={colors.colorFilterTitle}
                    />
                </Tooltip>
            </span>
        )
    }

    const isSwapDisabled = (item, type, isLimitExceeded) =>
        item?.value === serverMetrics.swap_usage.value &&
    selectedItem?.value === os.windows.value &&
    !isItemUsageLimitExceeded(type, isLimitExceeded, item)

    const isItemUsageLimitExceeded = (type, isLimitExceeded, childItem) => {
        return (
            isLimitExceeded &&
      !props.selectedMetricTypes?.[type]?.child_items.some(
          (item) => item?.value === childItem.value
      )
        )
    }

    const isChildItemDisabled = (childItem, type, isLimitExceeded) =>
        disabled ||
    isSwapDisabled(childItem, type, isLimitExceeded) ||
    isItemUsageLimitExceeded(type, isLimitExceeded, childItem)

    const getDisabledTooltip = (childItem, type, isLimitExceeded) => {
        if (isSwapDisabled(childItem, type, isLimitExceeded)) {
            return childItem?.swap_usage_unix_text
        }
        if (isItemUsageLimitExceeded(type, isLimitExceeded, childItem)) {
            return text.usage_limit_exceeded
        }
        return ''
    }

    const getInitialValues = () => {
        const stepInitialValues = formType?.initialValues || {}
        defaultFormValues?.map(({name, value, options}) => {
            stepInitialValues[name] =
        typeof value === 'object' ? {...value, options} : value
        })

        Object.keys(monitoringTypeData) &&
      setInitialValuesMap((prevMap) => ({
          ...prevMap,
          [Object.keys(monitoringTypeData)]:
          initialValuesMap?.[Object.keys(monitoringTypeData)] ||
          stepInitialValues
      }))
    }

    const getCurrentStepInitialValues = () => {
        return initialValuesMap?.[Object.keys(monitoringTypeData)] || {}
    }

    const handleFormFields = (name, value, setFieldValue) => {
        setFieldValue(name, value)
        name === 'os' && setSelectedItem(value)
    }

    useEffect(() => {
        if (monitoringType === serviceTypes.selection_type) {
            getSelectionTypeElements()
            return
        }
        setSelectionTypeElements([])

        if (monitoringType === serviceTypes.chart_type) {
            getChartTypeElements()
            return
        }
        setChartTypeElements([])

        if (monitoringType === serviceTypes.form_type) {
            getInitialValues()
        }
    }, [selectedItem, monitoringType, defaultFormValues])

    return (
        <>
            <div
                className={classNames('selection-container', {
                    'instruction-modal-container':
            monitoringType === serviceTypes.instructions_type
                })}
            >
                {monitoringType === serviceTypes.selection_type && (
                    <span className="selection-items">{generateSelectionType()}</span>
                )}
                {monitoringType === serviceTypes.form_type && (
                    <span className="selection-items">
                        <Formik
                            initialValues={getCurrentStepInitialValues()}
                            onSubmit
                            innerRef={formikRef}
                            enableReinitialize
                            validationSchema={() =>
                                formSchema(
                                    initialValuesMap?.[Object.keys(monitoringTypeData)] || {}
                                )
                            }
                            validateOnChange={false}
                            validateOnBlur={false}
                        >
                            {({values, setFieldValue}) => (
                                <DynamicForm
                                    formValues={values}
                                    setFieldValue={(name, value) =>
                                        handleFormFields(name, value, setFieldValue)
                                    }
                                    errors={errors}
                                    monitoringTypeData={formType?.items}
                                    isStepCompleted={props?.isStepCompleted}
                                    setIsNextButtonActive={props.setIsNextButtonActive}
                                    setInitialFormValues={setInitialFormValues}
                                    isLoading={isLoading}
                                    defaultFormValues={defaultFormValues}
                                    disabled={disabled}
                                />
                            )}
                        </Formik>
                    </span>
                )}
                {monitoringType === serviceTypes.instructions_type &&
          (isLoading ? (
              renderSkeletonLoaders(1, 'instruction-loader')
          ) : (
              <ConfigFileComponent
                  data={monitoringTypeData?.os_types}
                  language={selectedItem?.type}
                  selectedMetricTypes={props?.selectedMetricTypes}
                  url={props?.telemetryUrl}
              />
          ))}
                {monitoringType === serviceTypes.chart_type && (
                    <span className="selection-items">{generateChartType()}</span>
                )}
                {monitoringType === serviceTypes.metrics_type && (
                    <span
                        className={classNames('metrics-items', {
                            dark: isDarkMode
                        })}
                    >
                        {generateMetricsType()}
                    </span>
                )}

                {monitoringType === serviceTypes.rulesets_type && (
                    <span
                        className={classNames('selection-items', {
                            'selection-items-no-rulesets': !monitoringTypeData?.length
                        })}
                    >
                        <RulesetListView
                            data={monitoringTypeData}
                            rulesetUsageLimit={props?.rulesetUsageLimit}
                            showFormViewModal={props?.showFormViewModal}
                        />
                    </span>
                )}
            </div>
        </>
    )
}

export default MultiForm
